import { addAudioBreadcrumb } from "../../audio/audioSentry";
import { isOfflineAudioUrl } from "../../audio/resolveSource";
import type { createLocalSongAssets } from "../../data/localAudioPaths";
import { logger } from "@utils/logger";
import type { StrictNamedSet, PlayerStore } from "../types";
import type { QueueItem } from "../../utils/queue.types";

const audioSourceLabel = (url: string | null | undefined) => {
  if (!url) return "missing";
  return isOfflineAudioUrl(url) ? "local" : "remote";
};

const hasSetListEntries = (item: QueueItem) =>
  (item.metadata.setList?.entries?.length ?? 0) > 0;

/**
 * Finds the live queue item matching `item` (by qid + song id, across the
 * active queue and, for playlists, the pre-shuffle `originalOrder`) and
 * replaces its metadata with `resolvedItem.metadata`. Exported for reuse by
 * `playback.slice.ts`'s `ensureSongPlayable` integration — after a poll
 * gate re-fetches a song's row (see `features/onDemandPublish`), the fresh
 * metadata needs to land back in the live queue the same way local-asset
 * substitution's resolved metadata does here.
 */
export const updateQueueItemMetadata = (
  item: QueueItem,
  resolvedItem: QueueItem,
  set: StrictNamedSet<PlayerStore>,
  action: string,
) => {
  set(
    (state) => {
      const queue =
        state.activeQueueType === "playlist"
          ? state.playlistQueue
          : state.foryouQueue;
      if (!queue) return;

      const updateItem = (candidate: QueueItem) => {
        if (
          candidate.qid === item.qid &&
          candidate.metadata.id === item.metadata.id
        ) {
          candidate.metadata = resolvedItem.metadata;
        }
      };

      queue.items.forEach(updateItem);
      if (queue.type === "playlist") {
        queue.shuffleState.originalOrder?.forEach(updateItem);
      }
    },
    false,
    action,
  );
};

export const resolveQueueItemForPlayback = (
  item: QueueItem,
  set: StrictNamedSet<PlayerStore>,
  operation: "play" | "playWithStartTime",
  substitute: ReturnType<
    typeof createLocalSongAssets
  >["substituteLocalSongAssets"],
): QueueItem => {
  const [resolvedMetadata] = substitute([item.metadata]);
  if (resolvedMetadata === item.metadata) return item;

  const resolvedItem = { ...item, metadata: resolvedMetadata };
  const sourceBefore = audioSourceLabel(item.metadata.urlOnline);
  const sourceAfter = audioSourceLabel(resolvedMetadata.urlOnline);
  const hadSetListBefore = hasSetListEntries(item);
  const hasSetListAfter = hasSetListEntries(resolvedItem);

  updateQueueItemMetadata(
    item,
    resolvedItem,
    set,
    `playback/${operation}/resolveLocalAssets`,
  );

  if (sourceBefore !== sourceAfter || hadSetListBefore !== hasSetListAfter) {
    const attributes = {
      operation,
      songId: item.metadata.id,
      queueItemId: item.qid,
      sourceBefore,
      sourceAfter,
      hadSetListBefore,
      hasSetListAfter,
    };
    addAudioBreadcrumb("rnap.queue_item_resolved_for_playback", attributes);
    logger.info(
      "player",
      "player.queue_item_resolved_for_playback",
      attributes,
    );
  }

  return resolvedItem;
};
